/**
 * Chat Result Item
 *
 * One matching conversation in the command palette's Chats zone. Deliberately
 * the same shape as {@link SearchResultItem} — icon | title | time, snippet
 * beneath — so the palette reads as one system rather than as two features that
 * happen to share a dialog.
 *
 * **On the highlight delimiters.** `SearchResultItem` parses `«»` from its own
 * `ts_headline` call. Chat snippets use Unicode private-use code points instead,
 * because a chat message is arbitrary prose: a researcher quoting French, or
 * pasting text containing guillemets, would get mis-highlighted output. Note
 * titles rarely contain them; chat messages plausibly do. The divergence is
 * deliberate and the two should converge on private-use eventually — the
 * rendering is identical either way, so this is an internal difference only.
 *
 * The snippet is split into React text nodes and never injected as markup. It
 * is user and model content, so treating it as HTML would be an XSS hole in the
 * one surface that displays other people's words back to them.
 */

import type { ReactNode } from 'react';
import { HStack, VStack, Text } from '@chakra-ui/react';
import { Command } from 'cmdk';
import { stripMarkdownFragment } from '@bower/shared/utils/strip-markdown';
import { MaterialIcon } from '../../ui/MaterialIcon';
import {
  SNIPPET_HIGHLIGHT_START,
  SNIPPET_HIGHLIGHT_END,
  type ChatSearchResult,
} from '../../../services/agentChat.service';
import { formatTimeAgoShort } from '../../../utils/timeUtils';

interface ChatResultItemProps {
  result: ChatSearchResult;
  onSelect: (result: ChatSearchResult) => void;
}

/**
 * Split a `ts_headline` snippet into plain text and emphasised matches.
 *
 * An unpaired start delimiter emits the remainder as plain text rather than
 * dropping it — a truncated snippet should lose its emphasis, not its words.
 */
export function renderChatSnippet(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const startIdx = remaining.indexOf(SNIPPET_HIGHLIGHT_START);
    if (startIdx === -1) {
      nodes.push(remaining);
      break;
    }
    if (startIdx > 0) nodes.push(remaining.substring(0, startIdx));

    const endIdx = remaining.indexOf(SNIPPET_HIGHLIGHT_END, startIdx);
    if (endIdx === -1) {
      nodes.push(remaining.substring(startIdx + SNIPPET_HIGHLIGHT_START.length));
      break;
    }
    nodes.push(
      <Text as="span" fontWeight="semibold" color="text.secondary" key={key++}>
        {remaining.substring(startIdx + SNIPPET_HIGHLIGHT_START.length, endIdx)}
      </Text>,
    );
    remaining = remaining.substring(endIdx + SNIPPET_HIGHLIGHT_END.length);
  }

  return nodes;
}

/** Untitled conversations exist — titling is best-effort and runs after the turn. */
const FALLBACK_TITLE = 'Conversation';

export function ChatResultItem({ result, onSelect }: ChatResultItemProps) {
  const title = result.title?.trim() || FALLBACK_TITLE;

  return (
    <Command.Item
      // Prefixed so a session id can never collide with an entity result's id
      // in cmdk's flat value space, which would make one row un-selectable.
      value={`chat:${result.session_id}:${result.message_id}`}
      onSelect={() => onSelect(result)}
      style={{ cursor: 'pointer' }}
    >
      <HStack px={3} py={3} spacing={3} w="100%" align="flex-start">
        <MaterialIcon name="forum" boxSize={5} gradient="warm" />

        <VStack flex={1} minW={0} spacing={0} align="stretch">
          <HStack spacing={0} minW={0}>
            <Text fontSize="sm" color="text.primary" noOfLines={1} flexShrink={1} minW={0}>
              {title}
            </Text>
            {/* Whose line matched. Without it a snippet reads as the answer even
                when it is the question the user themselves asked. A recent
                chat (no query) has no matched line, so no label. */}
            {result.snippet && (
              <Text fontSize="xs" color="text.muted" flexShrink={0} whiteSpace="nowrap" ml={1.5}>
                {result.role === 'user' ? 'you' : 'Bower'}
              </Text>
            )}
          </HStack>
          {result.snippet && (
            <Text fontSize="xs" color="text.muted" noOfLines={1} lineHeight="short">
              {renderChatSnippet(stripMarkdownFragment(result.snippet))}
            </Text>
          )}
        </VStack>

        {result.timestamp ? (
          <Text fontSize="xs" color="text.muted" flexShrink={0}>
            {formatTimeAgoShort(new Date(result.timestamp * 1000))}
          </Text>
        ) : null}
      </HStack>
    </Command.Item>
  );
}
